import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Button from "../../../Subcomponents/Button";
import ConfirmationAlert from "../../../Subcomponents/ConfirmationAlert";
import {
  addConsentTemplate,
  deleteConsentTemplate,
  getConsentTemplates,
  updateConsentTemplate,
} from "../../../Services/Settings";
import { errorTost, successTost } from "../../../Services/Toast";
import AddConsentModal, { CONSENT_TYPES } from "./AddConsentModal";
import { esignSummary } from "./esignRules";
import cssClasses from "./CustomerConsentSettings.module.css";

const typeLabel = (type) => CONSENT_TYPES.find((t) => t.type === type)?.label || type;
const stripHtml = (html) =>
  (html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

// One consent per type. A consent that exists is asked in the Customer Portal
// (before token payment, allotment payment or showing virtual account details).
export default function CustomerConsentSettings() {
  const loggedInUser = useSelector((state) => state.Auth.user);
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { consent } (consent null = add)
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const usedTypes = consents.map((c) => c.type);
  const freeTypes = CONSENT_TYPES.filter((t) => !usedTypes.includes(t.type));

  useEffect(() => {
    loadConsents();
  }, []);

  function loadConsents() {
    setLoading(true);
    getConsentTemplates()
      .then((rows) => setConsents(rows || []))
      .catch((err) => {
        console.log(err);
        errorTost("Unable to load customer consents");
      })
      .finally(() => setLoading(false));
  }

  function saveConsent({ type, title, content, esignRules }) {
    const editing = modal?.consent;
    const obj = {
      title,
      content,
      updatedBy: loggedInUser?.userId ? String(loggedInUser.userId) : null,
    };
    if (type === "allotment" && esignRules) obj.esignRules = esignRules;
    setSaving(true);
    const request = editing
      ? updateConsentTemplate(editing.consentTemplateId, obj)
      : addConsentTemplate({ ...obj, type });
    request
      .then(() => {
        successTost(editing ? "Consent updated" : "Consent added");
        setModal(null);
        loadConsents();
      })
      .catch((err) => {
        console.log(err);
        errorTost(
          err?.code === "23505"
            ? `A consent for ${typeLabel(type)} already exists`
            : "Unable to save consent"
        );
      })
      .finally(() => setSaving(false));
  }

  function confirmDelete(value) {
    const consent = toDelete;
    setToDelete(null);
    if (value !== "confirmed" || !consent) return;
    deleteConsentTemplate(consent.consentTemplateId)
      .then(() => {
        successTost("Consent deleted");
        loadConsents();
      })
      .catch((err) => {
        console.log(err);
        errorTost("Unable to delete consent");
      });
  }

  return (
    <div className={cssClasses.wrapper}>
      <div className={cssClasses.topRow}>
        <p className={cssClasses.intro}>
          Add a consent for a type to ask for it in the Customer Portal. The customer reads the text, ticks "I agree"
          and verifies with an OTP sent to their registered mobile. It is asked only once per customer for each type.
        </p>
        <Button
          style={{
            width: "fit-content",
            color: "#FFFFFF",
            backgroundColor: "#155EEF",
            fontSize: "1.4rem",
            lineHeight: "2rem",
            whiteSpace: "nowrap",
          }}
          btnName={"+ Add Consent"}
          disable={loading || freeTypes.length === 0}
          clicked={() => setModal({ consent: null })}
        />
      </div>

      {loading ? (
        <div className={cssClasses.loader}>
          <CircularProgress />
        </div>
      ) : consents.length === 0 ? (
        <div className={cssClasses.empty}>
          No consent added yet. Customers will not be asked for any consent.
        </div>
      ) : (
        consents.map((consent) => (
          <div key={consent.consentTemplateId} className={cssClasses.card}>
            <div className={cssClasses.cardHead}>
              <div className={cssClasses.cardHeadText}>
                <span className={cssClasses.typeChip}>{typeLabel(consent.type)}</span>
                <div className={cssClasses.cardTitle}>{consent.title}</div>
              </div>
              <div className={cssClasses.actions}>
                <Tooltip title="Edit">
                  <IconButton onClick={() => setModal({ consent })}>
                    <EditOutlinedIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton onClick={() => setToDelete(consent)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
            <div className={cssClasses.preview}>{stripHtml(consent.content)}</div>
            {consent.type === "allotment" && esignSummary(consent.esignRules) && (
              <div className={cssClasses.esignSummary}>{esignSummary(consent.esignRules)}</div>
            )}
            <div className={cssClasses.meta}>Last updated: {formatDate(consent.updated_at)}</div>
          </div>
        ))
      )}

      {modal && (
        <AddConsentModal
          open={!!modal}
          consent={modal.consent}
          availableTypes={modal.consent ? CONSENT_TYPES.filter((t) => t.type === modal.consent.type) : freeTypes}
          saving={saving}
          handleClose={() => setModal(null)}
          onSave={saveConsent}
        />
      )}

      {toDelete && (
        <ConfirmationAlert
          open={!!toDelete}
          close={() => setToDelete(null)}
          title="Delete consent?"
          subTitle={`Customers will no longer be asked for the ${typeLabel(
            toDelete.type
          )}. Consents already given by customers are kept.`}
          buttonTitle="Cancel"
          returnRespButton="Delete"
          confirm={confirmDelete}
          hideCloseIcon
        />
      )}
    </div>
  );
}
